import { useState, useRef, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Tabs, TabsItem } from '@eventbrite/marmalade';

import { Tab } from './Tab';
import { Slots } from './Slots/Slots';
import { calculateScrollState, findNextTabToScroll, findPrevTabToScroll } from './scrollUtils';

import styles from './Content.module.scss';

export const MAX_SLOTS_SHOWN_BY_DEFAULT = 3;

interface Props {
  tabs: Tab[];
  showTabs?: boolean;
  showAllSlots: boolean;
  onTabChange: (tab: Tab) => void;
  onScrollStateChange?: (canScrollPrev: boolean, canScrollNext: boolean) => void;
}

export interface ContentScrollHandle {
  scrollToNext: () => void;
  scrollToPrev: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
}

export const Content = forwardRef<ContentScrollHandle, Props>(
  ({ tabs, showTabs, showAllSlots, onTabChange, onScrollStateChange }, ref) => {
    const [activeTab, setActiveTab] = useState<Tab>(tabs[0]);
    const tabsWrapperRef = useRef<HTMLDivElement>(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const getTabListElement = (): HTMLElement | null => {
      if (!tabsWrapperRef.current) return null;
      return tabsWrapperRef.current.querySelector('[role="tablist"]') as HTMLElement;
    };

    const getTabElements = (): HTMLElement[] => {
      const tabList = getTabListElement();
      if (!tabList) return [];
      return Array.from(tabList.querySelectorAll('[role="tab"]')) as HTMLElement[];
    };

    // Determines if scrolling is possible in either direction and updates arrow visibility
    const checkScrollState = useCallback(() => {
      const tabList = getTabListElement();
      if (!tabList) return;

      const { scrollLeft, scrollWidth, clientWidth } = tabList;
      const { canScrollPrev: newCanScrollPrev, canScrollNext: newCanScrollNext } = calculateScrollState(
        scrollLeft,
        scrollWidth,
        clientWidth,
      );

      setCanScrollPrev(newCanScrollPrev);
      setCanScrollNext(newCanScrollNext);

      // Notify parent of scroll state changes
      onScrollStateChange?.(newCanScrollPrev, newCanScrollNext);
    }, [onScrollStateChange]);

    const getNextTabToScroll = (): HTMLElement | null => {
      const tabList = getTabListElement();
      const tabElements = getTabElements();
      return findNextTabToScroll(tabList, tabElements);
    };

    const getPrevTabToScroll = (): HTMLElement | null => {
      const tabList = getTabListElement();
      const tabElements = getTabElements();
      return findPrevTabToScroll(tabList, tabElements);
    };

    const scrollToNext = () => {
      const nextTab = getNextTabToScroll();
      if (nextTab) {
        nextTab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    };

    const scrollToPrev = () => {
      const prevTab = getPrevTabToScroll();
      if (prevTab) {
        prevTab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    };

    useImperativeHandle(ref, () => ({
      scrollToNext,
      scrollToPrev,
      canScrollPrev,
      canScrollNext,
    }));

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        const tabList = getTabListElement();
        if (!tabList) return;

        checkScrollState();

        tabList.addEventListener('scroll', checkScrollState);
        window.addEventListener('resize', checkScrollState);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        const tabList = getTabListElement();
        if (tabList) {
          tabList.removeEventListener('scroll', checkScrollState);
        }
        window.removeEventListener('resize', checkScrollState);
      };
    }, [tabs, checkScrollState]);

    if (tabs.length === 0) {
      return null;
    }

    const slotsToShow = showAllSlots ? activeTab.slots : activeTab.slots?.slice(0, MAX_SLOTS_SHOWN_BY_DEFAULT);

    if (tabs.length === 1 && !showTabs) {
      return <Slots id={activeTab.name} slots={slotsToShow} />;
    }

    return (
      <>
        <div ref={tabsWrapperRef} className={styles.tabsWrapper}>
          <Tabs
            aria-label="Account options"
            defaultValue={activeTab.name}
            className={styles.tabs}
            onValueChange={(value) => {
              const newActiveTab = tabs.find((tab) => tab.name === value) as Tab;
              setActiveTab(newActiveTab);
              onTabChange(newActiveTab);
            }}
          >
            {tabs.map((tab, index) => (
              <TabsItem key={tab.name + index} value={tab.name} className={styles.tab}>
                {tab.name}
              </TabsItem>
            ))}
          </Tabs>
        </div>
        <Slots id={activeTab.name} slots={slotsToShow} />
      </>
    );
  },
);

Content.displayName = 'Content';
